import { NavigationBuilder } from './NavigationBuilder';
import type { UserPermissionType } from '../services/AuthService/types';
import { BaseFeatureToggles } from './BaseFeatureToggles';
import type { SecurityOptIn } from '@shipium-corp/shipium-ts-types';
import { getAdminConsoleBaseUrl } from '~/layout/utils';

export const shipiumNavigationBuilder = (
  partnerUrlSlug: string,
  loggedInUserRole: UserPermissionType,
  isShipiumUser: boolean,
  partnerFeatureToggles: Record<string, string>,
  partnerOptins: Record<string, SecurityOptIn>,
  isEu?: boolean,
) =>
  new NavigationBuilder(partnerUrlSlug, loggedInUserRole, isShipiumUser, partnerFeatureToggles, partnerOptins, isEu)
    .addLink({ name: 'Dashboard', path: '', icon: 'dashboard' })
    .addLink({
      name: 'Print Labels',
      path: 'print-labels',
      icon: 'printer',
      hideInEu: true,
    })
    .addSubMenu({ name: 'Team Management', path: 'team-management', icon: 'user' }, [
      {
        name: 'Manage Users',
        path: 'manage-users',
        requiredPermission: 'administrator',
        requiredOptinMap: { 'partner-sso-authentication-opt-in': false },
      },
      {
        name: 'Manage Users',
        path: 'sso-users',
        requiredPermission: 'administrator',
        requiredOptinMap: { 'partner-sso-authentication-opt-in': true },
      },
      { name: 'My Account', path: 'my-account' },
      {
        name: 'User Password Settings',
        path: 'partner-security-configuration',
        requiredOptinMap: { 'partner-sso-authentication-opt-in': false },
      },
    ])
    .addSubMenu({ name: 'Configure', path: 'configure', icon: 'setting' }, [
      {
        name: 'Fulfillment Configuration',
        path: 'configuration-v2',
        requiredPermission: 'viewer',
        requiredFeatureMap: { [BaseFeatureToggles.UI_ORTHO_V2]: true },
      },
      {
        name: 'Fulfillment Configuration Tree',
        path: 'configuration-v2/configuration-tree',
        requiredPermission: 'viewer',
        requiredFeatureMap: { [BaseFeatureToggles.UI_ORTHO_V2]: true },
      },
      {
        name: 'Fulfillment Contexts',
        ignoreSubMenuPath: true,
        path: 'fulfillment-contexts',
        requiredFeatureMap: { [BaseFeatureToggles.UI_ORTHO_V2]: true },
      },
      {
        name: 'Partner Config',
        path: 'partner',
        requiredPermission: 'administrator',
      },
      {
        name: 'Configurable Ship Options',
        path: 'ship-options',
        requiredPermission: 'administrator',
      },
      {
        name: 'Access Keys',
        path: 'access-keys',
        requiredPermission: 'administrator',
      },
      {
        name: 'Packaging Configuration',
        path: 'packaging-configuration',
        requiredPermission: 'administrator',
      },
      {
        name: 'Time In Transit Management',
        tag: 'beta',
        path: 'time-in-transit-management',
        shipiumAdmin: true,
      },
      {
        name: 'FedEx Account Registration',
        path: 'fedex-account-registration',
        requiredPermission: 'administrator',
        hideInEu: true,
      },
      {
        name: 'FedEx Offline Labels EULA',
        path: 'fedex-offline-labels-agreement',
        requiredPermission: 'administrator',
        requiredOptinMap: { 'offline-labels-opt-in': true },
        hideInEu: true,
      },
      {
        name: 'Amazon Account Registration',
        path: 'amazon-account-registration',
        shipiumAdmin: true,
        hideInEu: true,
      },
      {
        name: 'UPS Account Registration',
        path: 'ups-account-registration',
        requiredPermission: 'administrator',
        requiredFeatureMap: { [BaseFeatureToggles.UPS_VERIFY]: true },
        hideInEu: true,
      },
      {
        name: 'TikTokShop Account Registration',
        path: 'tiktok-account-registration',
        shipiumAdmin: true,
        hideInEu: true,
      },
    ])
    .addSubMenu({ name: 'Carrier Selection', path: 'carrier-selection', icon: 'truck' }, [
      {
        name: 'Shipment Count Limits',
        path: 'carrier-limits-v2',
        requiredFeatureMap: { [BaseFeatureToggles.UI_ORTHO_V2]: true },
      },
      {
        name: 'Shipment Cost Limits',
        path: 'shipment-cost-limits',
        requiredFeatureMap: { [BaseFeatureToggles.COSTING_LIMITS]: true },
      },
      {
        name: 'Shipment Dynamic Limits',
        path: 'dynamic-limits',
        requiredFeatureMap: { [BaseFeatureToggles.DYNAMIC_LIMITS]: true },
        hideInEu: true,
      },
      {
        name: 'Carrier Selection Shipping Report',
        path: 'delivery-reporting/shipment-volume',
        ignoreSubMenuPath: true,
        hideInEu: true,
      },
      {
        name: 'Summary Manifest Report',
        path: 'summary-manifest-report',
        requiredFeatureMap: { [BaseFeatureToggles.AUTO_MANIFEST]: false },
        hideInEu: true,
      },
      {
        name: 'Manifest',
        path: 'manifest',
        requiredFeatureMap: { [BaseFeatureToggles.AUTO_MANIFEST]: true },
        hideInEu: true,
      },
      {
        name: 'Bulk Label Void',
        path: 'bulk-label-void',
      },
      {
        name: 'Shipment Export',
        path: 'export',
        requiredPermission: 'editor',
        requiredOptinMap: { 'carrier-selection-opt-in': true },
        hideInEu: true,
      },
      {
        name: 'Carrier Selection Rules',
        path: 'rulesets',
        requiredPermission: 'administrator',
        requiredFeatureMap: { [BaseFeatureToggles.RULE_SETS]: true },
      },
      {
        name: 'Carrier Selection Webhooks',
        path: 'carrier-selection-webhooks',
        requiredPermission: 'administrator',
        requiredFeatureMap: { [BaseFeatureToggles.CS_WEBHOOKS]: true },
        hideInEu: true,
      },
    ])
    .addSubMenu(
      {
        name: 'Delivery Reporting',
        path: 'delivery-reporting',
        icon: 'line-chart',
      },
      [
        {
          name: 'Shipment Search',
          path: 'shipment-search',
        },
        {
          name: 'Shipment Volume',
          path: 'shipment-volume',
          hideInEu: true,
        },
      ],
    )
    .addSubMenu(
      {
        name: 'Simulation',
        path: 'simulation',
        icon: 'experiment',
        requiredOptinMap: { 'simulation-viewer-opt-in': true },
        hideInEu: true,
      },
      [
        {
          name: 'Simulation',
          path: '',
        },
        {
          name: 'Uploads',
          path: 'upload',
          tag: 'beta',
          shipiumAdmin: true,
        },
        {
          name: 'Sell Rate Sheets',
          tag: 'beta',
          path: 'sell-rate-sheets',
          shipiumAdmin: true,
        },
      ],
    )
    .addAdminMenu({ name: 'Shipium Admin', path: 'shipium-admin', icon: 'tool-outlined' }, [
      { name: 'Rate Calculation Test', path: 'rate-calculation-test' },
      { name: 'Initial Uploads', path: 'uploads' },
      {
        name: 'Opt-ins & Feature Toggles',
        path: `${getAdminConsoleBaseUrl()}/partner-config/opt-ins-toggles`,
        external: true,
      },
      // TODO(2026-07-09): Remove this entry once the Admin Console cutover is complete. (PIP-973)
      { name: 'Security Management', path: 'security-management' },
      { name: 'Send Notifications', path: 'send-notifications' },
      { name: 'Zip Zone Settings', path: 'zip-zone-map' },
      { name: 'Configuration Asset Inspector', path: 'configure/assets' },
      { name: 'Bulk Asset Processing', path: 'bulk-asset-processing' },
    ])
    .addAdminMenu(
      { name: 'Shipium Dev', path: 'shipium-dev', icon: 'experiment' },
      [
        {
          name: 'Admin',
          path: 'admin',
          group: 'Admin',
          links: [
            {
              name: 'Configuration Audit',
              path: 'configure/configuration-audit',
              requiredPermission: 'administrator',
              requiredFeatureMap: { [BaseFeatureToggles.UI_ORTHO_V2]: false },
            },
            { name: 'Partner Configuration V2', path: 'configure/partner-config' },
          ],
        },
        {
          name: 'Demo',
          path: 'demo',
          group: 'Demo',
          links: [
            { name: 'Bulk Asset Processing', path: 'shipium-admin/bulk-asset-processing' },
            {
              name: 'Fulfillment Engine',
              path: 'fulfillment-engine',
              links: [
                {
                  name: 'Fulfillment Engine Search',
                  path: 'fulfillment-engine',
                },
                {
                  name: 'Fulfillment Engine Configuration',
                  path: 'fulfillment-engine-configuration',
                },
              ],
            },
            { name: 'Fulfillment Contexts', path: 'fulfillment-contexts' },
            {
              name: 'Subscription Test Page',
              path: 'shipium-admin/subscription-test',
            },
            {
              name: 'Carrier Costing By Weight',
              path: 'shipium-admin/cost-by-weight',
            },
            { name: 'ZPL Preview Tool', path: 'shipium-admin/zpl-preview' },
            { name: 'Document Blueprints', path: 'shipium-admin/document-blueprints' },
            { name: 'Promise Explorer', path: 'shipium-admin/promise-explorer', shipiumAdmin: true },
            {
              name: 'Carrier Selection Packaging Report',
              path: 'carrier-selection/carrier-selection-packaging-report',
            },
          ],
        },
      ],
      true,
    )
    .addSubMenu(
      {
        name: 'Tracking',
        path: 'tracking',
        icon: 'compass',
        hideInEu: true,
      },
      [
        { name: 'Track Shipment', path: '', requiredOptinMap: { 'carrier-tracking-api-opt-in': true } },
        {
          name: 'Tracking Webhooks',
          path: 'webhooks',
          requiredOptinMap: { 'tracking-opt-in': true },
          requiredPermission: 'administrator',
        },
      ],
    );
